import type { Ref } from 'vue';
import type { SearchBoxInterface } from '@hypercodestudio/basler-components';
import { buildUrlString } from '~/utils/buildUrlString';
import { useSearchHistoryStore } from '~/stores/globalSearch/searchHistory';

interface QuerySuggestion {
  query: string;
  popularity: number;
  'categories.level0'?: string | string[];
  'categories.level1'?: string | string[];
  magento_emea_de_products?: {};
  // Allow for any property that might contain magento and products
  [key: string]: any;
}

interface QuerySuggestionsResponse {
  results: QuerySuggestion[];
}

/**
 * Helper function to check if a suggestion has any property containing both "magento" and "products"
 */
function hasMagentoProductsProperty(suggestion: QuerySuggestion): boolean {
  return Object.keys(suggestion).some(
    (key) =>
      key.toLowerCase().includes('magento') &&
      key.toLowerCase().includes('products')
  );
}

export function useSearchSuggestions() {
  const isSearching = ref(false);
  const searchResults = ref<QuerySuggestion[]>([]);
  const hasMoreResults = ref(false);
  const suggestionGroups = ref([]) as Ref<
    SearchBoxInterface['suggestionGroups']
  >;
  const locale = useRouteParamLocale();

  const { $globalPageSettings, $textDictionary } = useNuxtApp();

  const searchBoxArgs: Ref<SearchBoxInterface> = ref({
    introText:
      $textDictionary.value['searchbox.introText.label'] ??
      "Explore Basler's world of hardware, software, expert resources, and more – tailored for your technical needs.",
    inputLabelText:
      $textDictionary.value['searchbox.inputLabel.label'] ??
      'Search for products and resources',
    inputPlaceholderText:
      $textDictionary.value['searchbox.inputPlaceholderText.label'] ??
      'Search for products and resources',
    submitSearchBtnText:
      $textDictionary.value['searchbox.submitSearchBtnText.label'] ?? 'Search',
    clearSearchBtnText:
      $textDictionary.value['searchbox.clearSearchBtnText.label'] ??
      'Clear search',
    clearSuggestionItemBtnText:
      $textDictionary.value['searchbox.clearSuggestionItemBtnText.label'] ??
      'Remove previous search for',
    suggestionsListTitle:
      $textDictionary.value['searchbox.suggestionsListTitle.label'] ??
      'Suggested results for your search'
  });

  // Initialize the search history store
  const searchHistoryStore = useSearchHistoryStore();

  // Create a computed property for the search history tags
  const searchHistoryTags = computed(() => {
    return searchHistoryStore.recentSearches.map((entry) => ({
      text: entry.text,
      iconName: 'clock',
      id: entry.id,
      showCloseIcon: true,
      closeIconName: 'bin',
      closeIconScaleFactor: 1.2,
      isDeletable: true,
      link: {
        uri: $globalPageSettings.value?.searchResultsPage?.slug
          ? buildUrlString(
              locale.value,
              $globalPageSettings.value?.searchResultsPage.slug,
              `q=${entry.text}`,
              undefined,
              false
            )
          : '#'
      }
    }));
  });

  // Create a computed property for the tagListGroupArgs to make it reactive
  const tagListGroupArgs = computed(() => {
    const resultItems = [];
    if (searchHistoryTags.value.length) {
      resultItems.push({
        title:
          $textDictionary.value['searchbox.previousSearches.label'] ??
          'Previous searches',
        tags: searchHistoryTags.value
      });
    }
    if (
      $globalPageSettings?.value?.searchPopularProducts?.productRecommendations
        ?.length
    ) {
      resultItems.push({
        title:
          $textDictionary.value['searchbox.popularProducts.label'] ??
          'Popular products',
        alignment: 'evenly',
        size: 'medium',
        tags: $globalPageSettings?.value?.searchPopularProducts?.productRecommendations?.map(
          (entry: any) => {
            return {
              text: entry.title,
              iconName: 'action_bar_magnifier',
              image: {
                src: entry.imageUrl
              },
              link: $globalPageSettings.value?.searchResultsPage?.slug
                ? {
                    uri: buildUrlString(
                      locale.value,
                      $globalPageSettings.value?.searchResultsPage.slug,
                      `q=${entry.query}`,
                      undefined,
                      false
                    )
                  }
                : {}
            };
          }
        )
      });
    }

    if (
      $globalPageSettings?.value?.searchPopularSearchesCollection?.items?.length
    ) {
      resultItems.push({
        title:
          $textDictionary.value[
            'pageHeader.searchBox.popularSearches.title.label'
          ] ?? 'Popular searches',
        tags: $globalPageSettings?.value?.searchPopularSearchesCollection?.items?.map(
          (entry: any) => {
            return {
              text: entry.linkText,
              iconName: 'action_bar_magnifier',
              link: useBuildLinkInterface(entry, false)
            };
          }
        )
      });
    }

    return {
      headlineLevel: 3,
      items: resultItems
    };
  });

  /**
   * Maps search API results to the format expected by the SearchBox component
   */
  const mapSuggestionsToGroups = (
    suggestions: QuerySuggestion[],
    currentGroups: SearchBoxInterface['suggestionGroups'] = []
  ): SearchBoxInterface['suggestionGroups'] => {
    // Get search history entries
    const searchHistoryStore = useSearchHistoryStore();
    const searchHistoryItems = searchHistoryStore.recentSearches.map(
      (entry) => ({
        title: entry.text,
        iconBefore: 'clock',
        iconAfter: 'arrow-right',
        isDeletable: true,
        id: entry.id
      })
    );

    const productSuggestions: any[] = [];
    const generalSuggestions: any[] = [];

    suggestions.forEach((suggestion) => {
      const suggestionItem = {
        title: suggestion.query,
        iconBefore: 'magnifier',
        iconAfter: 'arrow-right'
      };

      if (hasMagentoProductsProperty(suggestion)) {
        let category;
        if (Array.isArray(suggestion['categories.level1'])) {
          const r = suggestion['categories.level1'][0] || '';
          category = r.split('///')[1].trim();
        } else {
          const r = suggestion['categories.level1'] || '';
          category = r.toString().split('///')[1]?.trim();
        }

        const itemWithBadge = {
          ...suggestionItem,
          badge: {
            text: category
          }
        };

        productSuggestions.push(itemWithBadge);
      } else {
        generalSuggestions.push(suggestionItem);
      }
    });

    const newGroups = [];

    // Always add search history as the first group if there are entries
    if (searchHistoryItems.length > 0) {
      newGroups.push({
        label:
          $textDictionary.value['searchbox.previousSearches.label'] ??
          'Previous searches',
        items: searchHistoryItems
      });
    }

    if (productSuggestions.length > 0) {
      newGroups.push({
        label:
          $textDictionary.value['searchbox.productsLabel.label'] ?? 'Products',
        items: productSuggestions
      });
    }

    if (generalSuggestions.length > 0) {
      newGroups.push({
        label:
          $textDictionary.value['searchbox.suggestedSearchesLabel.label'] ??
          'Suggested searches',
        items: generalSuggestions
      });
    }

    return newGroups;
  };

  /**
   * Handles search queries and fetches suggestions from the API
   */
  const onSearchSuggestions = async (query: string) => {
    if (!query || query.trim() === '') {
      return;
    }

    isSearching.value = true;

    try {
      const response = await $fetch<QuerySuggestionsResponse>(
        '/api/algolia/query-suggestions',
        {
          method: 'POST',
          body: {
            query: query.trim(),
            locale: locale.value,
            hitsPerPage: 10
          }
        }
      );

      searchResults.value = response.results;

      // Always map results, even if there are no API results
      // This ensures the search history group is added
      const mappedResults = mapSuggestionsToGroups(searchResults.value || []);
      suggestionGroups.value = mappedResults || [];
    } catch (error) {
      console.error('Error fetching search suggestions:', error);

      // Even on error, try to show search history
      const searchHistoryStore = useSearchHistoryStore();
      if (searchHistoryStore.recentSearches.length > 0) {
        const searchHistoryItems = searchHistoryStore.recentSearches.map(
          (entry) => ({
            title: entry.text,
            iconBefore: 'clock',
            iconAfter: 'arrow-right',
            isDeletable: true
          })
        );

        suggestionGroups.value = [
          {
            label:
              $textDictionary.value['searchbox.previousSearches.label'] ??
              'Previous searches',
            items: searchHistoryItems
          }
        ];
      }
    } finally {
      isSearching.value = false;
    }
  };

  return {
    isSearching,
    searchResults,
    hasMoreResults,
    suggestionGroups,
    searchBoxArgs,
    tagListGroupArgs,
    onSearchSuggestions
  };
}
